import { toast } from 'react-toastify'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import * as Sentry from '@sentry/react'
import { stakingAddress } from 'app/features/web3/web3.config'
import { disconnectWallet, getBalance } from 'app/features/web3/web3Slice'
import CustomError from 'app/utils/error'
import { failToast, showPendingToast, successToast } from 'app/utils/toast'
import { _1e18, getTokenPrice, number } from 'app/utils/web3'
import BN from 'bn.js'
import { getAction } from 'connected-react-router'
import { create } from 'domain'

import {
  APR,
  canStake,
  canWidthdraw,
  getCanGetReward,
  getClaimRewards,
  getEarned,
  getTimeLeft,
  userStakedTokens,
  userStakedValue,
} from './earn.module'

import type { RootState } from 'app/store'
export enum StakingEnabled {
  idle = 'idle',
  loading = 'loading',
  enabled = 'enabled',
  disabled = 'disabled',
}

export enum Status {
  idle = 'idle',
  loading = 'loading',
  failed = 'failed',
}

export interface EarnState {
  totalTokens?: number // Total tokens that are staked in contract
  initialStatus: Status
  stakingDataStatus: Status
  stakingEnabled: StakingEnabled
  stakedTokens?: number
  stakedValue?: number
  busdBalance?: number
  usdValue?: number
  apr?: number
  tokenPrice?: BN
  stakingStatus: Status
  unStakingStatus: Status
  timeLeft?: number
  canGetReward?: boolean
  earned?: number
  claimRewardStatus: Status
  rewardPerToken?: number
}

const initialState: EarnState = {
  initialStatus: Status.idle,
  stakingDataStatus: Status.idle,
  stakingEnabled: StakingEnabled.idle,
  stakingStatus: Status.idle,
  unStakingStatus: Status.idle,
  claimRewardStatus: Status.idle,
}

export const getStakingData = createAsyncThunk(
  'earn/getStakingData',
  async (_, { getState, dispatch }) => {
    const { staking, web3, reward, selectedAccount } = (getState() as RootState)
      .web3

    if (!web3 || !reward || !selectedAccount || !staking) {
      console.log(
        'web3',
        web3,
        'reward',
        reward,
        'selectedAccount',
        selectedAccount,
        'staking',
        staking
      )
      throw new CustomError('web3 error', {
        web3,
        reward,
        selectedAccount,
        staking,
      })
    }

    const tokenPrice = await getTokenPrice(web3)

    const rewardPerToken =
      Number(
        await staking.methods.rewardPerToken().call({ from: selectedAccount })
      ) /
      10 ** 18

    console.log('rewardPerToken', rewardPerToken)

    console.log('tokenPrice', tokenPrice)

    const totalTokens = await staking?.methods.totalSupply().call()
    console.log('total tokens', totalTokens)

    await dispatch(getBalance())

    const { busdBalance, apr } = await APR(
      web3,
      totalTokens,
      reward,
      tokenPrice,
      staking
    )

    console.log(`
        busdBalance: ${busdBalance}
        totalTokens: ${totalTokens}
        `)

    console.log('apr', apr)

    const stakedTokens = await userStakedTokens(web3, staking, selectedAccount)

    console.log('stakedTokens', stakedTokens)

    const stakedValue = await userStakedValue(
      web3,
      staking,
      selectedAccount,
      tokenPrice
    )

    console.log('stakedValue', Number(stakedValue))

    const timeLeft = Number(await getTimeLeft(staking, selectedAccount))

    const canGetReward = await getCanGetReward(staking, selectedAccount)
    console.log('canGetReward', canGetReward)

    const earned = await getEarned(staking, selectedAccount)

    return {
      totalTokens,
      busdBalance,
      stakedTokens,
      stakedValue,
      apr,
      tokenPrice,
      timeLeft,
      canGetReward,
      earned,
      rewardPerToken,
    }
  }
)

export const getInitialStakingData = createAsyncThunk(
  'earn/getInitialStakingData',
  async (_, { getState, dispatch }) => {
    await dispatch(getStakingData())
  }
)

export const enableStaking = createAsyncThunk(
  'earn/enableStaking',
  async (_, { getState }) => {
    const { token, web3, selectedAccount } = (getState() as RootState).web3

    if (!web3) {
      throw new Error('web3 error')
    }
    await token?.methods
      .approve(
        stakingAddress,
        number(
          web3,
          '115792089237316195423570985008687907853269984665640564039457584007913129639935'
        )
      )
      .send({
        from: selectedAccount,
      })
  }
)

export const stakeTokens = createAsyncThunk(
  'earn/stakeTokens',
  async (amount: number, { getState, dispatch, requestId }) => {
    const { staking, web3, selectedAccount, token } = (getState() as RootState)
      .web3

    showPendingToast(requestId, 'Staking...')

    const { tokenPrice } = (getState() as RootState).earn

    if (!web3 || !staking || !selectedAccount || !tokenPrice || !token) {
      throw new Error('web3 error')
    }

    if (amount == 0) {
      throw new Error(`Can't stake 0`)
    }

    const stakeAmount = number(web3, amount).mul(_1e18(web3))

    if (!(await canStake(stakeAmount, selectedAccount, staking))) {
      throw new Error(`Can't stake more than your balance`)
    }

    await token.methods.approve(stakingAddress, stakeAmount).send({
      from: selectedAccount,
    })

    await staking.methods.stake(stakeAmount).send({
      from: selectedAccount,
      gasLimit: 1000000,
    })

    const stakedTokens = await userStakedTokens(web3, staking, selectedAccount)

    const stakedValue = Number(
      await userStakedValue(web3, staking, selectedAccount, tokenPrice)
    )

    const earned = await getEarned(staking, selectedAccount)

    console.log('earned', earned)

    const timeLeft = Number(await getTimeLeft(staking, selectedAccount))

    await dispatch(getStakingData())

    return {
      stakedTokens,
      stakedValue,
      earned,
      timeLeft,
    }
  }
)

export const unStakeTokens = createAsyncThunk(
  'earn/unStakeTokens',
  async (amount: number, { getState, dispatch, requestId }) => {
    const { staking, web3, selectedAccount, token } = (getState() as RootState)
      .web3
    const { tokenPrice } = (getState() as RootState).earn

    showPendingToast(requestId, 'Unstaking...')

    if (!web3 || !staking || !selectedAccount || !tokenPrice || !token) {
      throw new Error('web3 error')
    }

    const unStakeAmount = number(web3, amount).mul(_1e18(web3))
    if (amount == 0) {
      throw new Error("Can't unstake 0 tokens")
    }

    if (
      (await canWidthdraw(unStakeAmount, selectedAccount, staking)) == false
    ) {
      throw new Error("can't unstake more than your staked tokens")
    }

    await token.methods.approve(stakingAddress, unStakeAmount).send({
      from: selectedAccount,
    })

    await staking.methods.unstake(unStakeAmount).send({
      from: selectedAccount,
      gasLimit: 1000000,
    })

    const stakedTokens = await userStakedTokens(web3, staking, selectedAccount)

    const stakedValue = Number(
      await userStakedValue(web3, staking, selectedAccount, tokenPrice)
    )

    const earned = await getEarned(staking, selectedAccount)

    const timeLeft = Number(await getTimeLeft(staking, selectedAccount))

    await dispatch(getStakingData())

    return {
      stakedTokens,
      stakedValue,
      earned,
      timeLeft,
    }
  }
)

export const userCanGetReward = createAsyncThunk(
  'earn/userCanGetReward',
  async (_, { getState }) => {
    const { staking, selectedAccount } = (getState() as RootState).web3

    if (!staking || !selectedAccount) {
      throw new Error('web3 error')
    }
    const canGetReward = await getCanGetReward(staking, selectedAccount)

    return {
      canGetReward,
    }
  }
)

export const claimRewards = createAsyncThunk(
  'earn/claimRewards',
  async (_, { getState, dispatch, requestId }) => {
    const { staking, selectedAccount, token } = (getState() as RootState).web3

    showPendingToast(requestId, 'Claiming rewards...')

    if (!staking || !selectedAccount) {
      throw new Error('web3 error')
    }

    if (!(await getCanGetReward(staking, selectedAccount))) {
      throw new Error("Can't get reward")
    }

    await getClaimRewards(staking, selectedAccount)

    await dispatch(getStakingData())
  }
)

export const earnSlice = createSlice({
  name: 'earn',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {},
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(getInitialStakingData.pending, (state) => {
        state.initialStatus = Status.loading
      })
      .addCase(getInitialStakingData.fulfilled, (state, action) => {
        return { ...state, initialStatus: Status.idle }
      })
      .addCase(getInitialStakingData.rejected, (state, action) => {
        const err = new Error(action.error.message)
        err.name = action.error.name || ''
        err.stack = action.error.stack

        Sentry.withScope(function (scope) {
          scope.setTag('case', action.type)
          scope.setExtra('payload', action.payload)
          scope.setExtra('error', err)
          Sentry.captureException(err)
        })
        state.initialStatus = Status.failed
      })
      // getStakingData
      .addCase(getStakingData.pending, (state) => {
        state.stakingDataStatus = Status.loading
      })
      .addCase(getStakingData.fulfilled, (state, action) => {
        return { ...state, ...action.payload, stakingDataStatus: Status.idle }
      })
      .addCase(getStakingData.rejected, (state, action) => {
        // const err = new CustomError(action.error.message)
        // err.name = action.error.name || ''
        // err.stack = action.error.stack

        Sentry.withScope(function (scope) {
          scope.setTag('case', action.type)
          scope.setExtra('payload', action.payload)
          scope.setExtra('error', action.error)
          Sentry.captureException(action.error)
        })
        state.stakingDataStatus = Status.failed
      })
      .addCase(disconnectWallet.fulfilled, () => {
        return initialState
      })
      // enableStaking
      .addCase(enableStaking.pending, (state) => {
        state.stakingEnabled = StakingEnabled.loading
      })
      .addCase(enableStaking.fulfilled, (state) => {
        state.stakingEnabled = StakingEnabled.enabled
      })
      .addCase(enableStaking.rejected, (state, action) => {
        const err = new Error(action.error.message)
        err.name = action.error.name || ''
        err.stack = action.error.stack

        Sentry.withScope(function (scope) {
          scope.setTag('case', action.type)
          scope.setExtra('payload', action.payload)
          scope.setExtra('error', err)
          Sentry.captureException(err)
        })
        state.stakingEnabled = StakingEnabled.disabled
      })
      // stakeTokens
      .addCase(stakeTokens.pending, (state) => {
        state.stakingStatus = Status.loading
      })
      .addCase(stakeTokens.fulfilled, (state, action) => {
        successToast(action.meta.requestId, 'Staking successful')
        return { ...state, ...action.payload, stakingStatus: Status.idle }
      })
      .addCase(stakeTokens.rejected, (state, action) => {
        failToast(
          action.meta.requestId,
          `Staking failed - ${action.error.message}`
        )
        const err = new Error(action.error.message)
        err.name = action.error.name || ''
        err.stack = action.error.stack

        Sentry.withScope(function (scope) {
          scope.setTag('case', action.type)
          scope.setExtra('payload', action.payload)
          scope.setExtra('error', err)
          Sentry.captureException(err)
        })

        state.stakingStatus = Status.idle
      })
      // unStakeTokens
      .addCase(unStakeTokens.pending, (state) => {
        state.unStakingStatus = Status.loading
      })
      .addCase(unStakeTokens.fulfilled, (state, action) => {
        successToast(action.meta.requestId, 'Unstaking successful')
        return { ...state, ...action.payload, unStakingStatus: Status.idle }
      })
      .addCase(unStakeTokens.rejected, (state, action) => {
        failToast(
          action.meta.requestId,
          `Staking failed - ${action.error.message}`
        )

        const err = new Error(action.error.message)
        err.name = action.error.name || ''
        err.stack = action.error.stack

        Sentry.withScope(function (scope) {
          scope.setTag('case', action.type)
          scope.setExtra('payload', action.payload)
          scope.setExtra('error', err)
          Sentry.captureException(err)
        })

        state.unStakingStatus = Status.idle
      })
      // userCanGetReward
      .addCase(userCanGetReward.pending, (state) => {
        state.unStakingStatus = Status.loading
      })
      .addCase(userCanGetReward.fulfilled, (state, action) => {
        return { ...state, ...action.payload, unStakingStatus: Status.idle }
      })
      .addCase(userCanGetReward.rejected, (state, action) => {
        const err = new Error(action.error.message)
        err.name = action.error.name || ''
        err.stack = action.error.stack

        Sentry.withScope(function (scope) {
          scope.setTag('case', action.type)
          scope.setExtra('payload', action.payload)
          scope.setExtra('error', err)
          Sentry.captureException(err)
        })
        state.unStakingStatus = Status.idle
      })
      // claimRewards
      .addCase(claimRewards.pending, (state) => {
        state.claimRewardStatus = Status.loading
      })
      .addCase(claimRewards.fulfilled, (state, action) => {
        successToast(action.meta.requestId, 'Rewards claimed')
        state.claimRewardStatus = Status.idle
      })
      .addCase(claimRewards.rejected, (state, action) => {
        failToast(action.meta.requestId, "Couldn't claim rewards")

        const err = new Error(action.error.message)
        err.name = action.error.name || ''
        err.stack = action.error.stack

        Sentry.withScope(function (scope) {
          scope.setTag('case', action.type)
          scope.setExtra('payload', action.payload)
          scope.setExtra('error', err)
          Sentry.captureException(err)
        })

        state.claimRewardStatus = Status.idle
      })
  },
})

export default earnSlice.reducer
